<script setup>
import {APP_STORE} from '~/utils/appLinks';
/**
 * AppCallout — "web or app, all features everywhere" section. Copy + feature
 * chips + store badges on the left, a phone frame (the #screen slot) on the
 * right showing a real component as if running in the app.
 */
const features = [
  'Enter & approve scores',
  'Live ladders & brackets',
  'Challenge notifications',
  'Your stats & history',
];
</script>

<template>
  <section class="ac">
    <div class="ac-glow" aria-hidden="true"/>
    <v-row align="center" class="ac-inner">
      <v-col cols="12" md="7">
        <span class="ac-eyebrow"><v-icon size="15" class="mr-1">mdi-cellphone-link</v-icon>Web &amp; mobile app</span>
        <h2 class="ac-title">All of Tap Leagues, in your pocket</h2>
        <p class="ac-sub">
          Everything you can do on the web you can do in the app. There's no cut-down version. Run your
          club from the sofa or settle a score courtside, and it all stays in sync.
        </p>

        <ul class="ac-feats">
          <li v-for="f in features" :key="f">
            <v-icon size="16" color="success" class="mr-2">mdi-check-circle</v-icon>{{ f }}
          </li>
        </ul>

        <div class="ac-stores">
          <a :href="APP_STORE.ios" target="_blank" rel="noopener">
            <img alt="Download on the Apple App Store" src="/images/apple_link.png" width="150"/>
          </a>
          <a :href="APP_STORE.android"
             target="_blank" rel="noopener">
            <img alt="Get it on Google Play" src="/images/google_link.png" width="160"/>
          </a>
        </div>
      </v-col>

      <v-col cols="12" md="5">
        <div class="ac-phone-wrap">
          <div class="ac-phone">
            <div class="ac-phone-screen" aria-hidden="true" inert>
              <div class="ac-phone-notch" aria-hidden="true"/>
              <!-- render at real phone logical width, then scale to fit the frame -->
              <div class="ac-phone-viewport">
                <slot name="screen"/>
              </div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </section>
</template>

<style scoped lang="scss">
.ac {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  padding: 40px 28px;
  background: rgb(var(--v-theme-brandTint));
  border: 1px solid rgb(var(--v-theme-borderLight));

  @media (min-width: 960px) {
    padding: 52px 56px;
  }
}

.ac-glow {
  position: absolute;
  z-index: 0;
  top: -40%;
  right: -8%;
  width: 460px;
  height: 460px;
  background: radial-gradient(circle, rgba(211, 41, 70, 0.18), transparent 65%);
  pointer-events: none;
}

.ac-inner {
  position: relative;
  z-index: 1;
}

.ac-eyebrow {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: rgb(var(--v-theme-primary));
}

.ac-title {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.6px;
  line-height: 1.15;
  margin-top: 8px;

  @media (min-width: 960px) {
    font-size: 32px;
  }
}

.ac-sub {
  font-size: 15px;
  line-height: 1.6;
  color: rgb(var(--v-theme-charcoal));
  margin-top: 12px;
  max-width: 460px;
}

.ac-feats {
  list-style: none;
  padding: 0;
  margin: 18px 0 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;

  @media (min-width: 600px) {
    grid-template-columns: 1fr 1fr;
  }

  li {
    display: flex;
    align-items: center;
    font-size: 13.5px;
    font-weight: 600;
    color: rgb(var(--v-theme-text));
  }
}

.ac-stores {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 24px;

  img {
    display: block;
    height: 44px;
    width: auto;
    border-radius: 8px;
  }
}

/* ── Phone frame (hosts live app components via #screen) ── */
.ac-phone-wrap {
  display: flex;
  justify-content: center;

  @media (min-width: 960px) {
    justify-content: flex-end;
  }
}

/* phone geometry — keep these in sync:
   inner screen width = phone width (290) - 2 * padding (11) = 268
   logical viewport = 390 x 844 (compete.html's native size)
   scale = 268 / 390 = 0.6872 → screen height = 844 * 0.6872 = 580 */
.ac-phone {
  position: relative;
  width: 290px;
  padding: 11px;
  border-radius: 44px;
  background: #1A1A1A;
  box-shadow:
    0 34px 64px -24px rgba(26, 26, 26, 0.55),
    0 12px 24px -16px rgba(26, 26, 26, 0.35);
}

.ac-phone-screen {
  position: relative;
  width: 268px;
  height: 580px;
  border-radius: 34px;
  overflow: hidden;
  background: rgb(var(--v-theme-light));
  pointer-events: none;
  user-select: none;
}

.ac-phone-notch {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 96px;
  height: 22px;
  border-radius: 0 0 14px 14px;
  background: #1A1A1A;
  z-index: 3;
}

.ac-phone-viewport {
  width: 390px;
  height: 844px;
  transform: scale(0.6872);
  transform-origin: top left;
}
</style>
